import React, { useState, useRef, useEffect } from 'react';
import styles from '../../styles/formComponent/DatePicker.module.scss';
import { CalendarIcon, XMarkIcon } from '@heroicons/react/24/outline';

const DatePicker = ({
  label,
  name,
  value = '',
  onChange,
  onBlur,
  onFocus,
  placeholder = 'DD/MM/YYYY',
  required = false,
  disabled = false,
  error,
  helperText,
  variant = 'outlined',
  size = 'medium',
  className = '',
  autoFocus = false,
  readOnly = false,
  allowClear = true,
  ...props
}) => {
  const [focused, setFocused] = useState(false);
  const inputRef = useRef(null);
  const hiddenInputRef = useRef(null);

  // Convert YYYY-MM-DD to DD/MM/YYYY for display
  const formatDateForDisplay = (dateValue) => {
    if (!dateValue) return '';
    try {
      // Handle both YYYY-MM-DD format and Date objects
      const date = typeof dateValue === 'string' ? new Date(dateValue + 'T00:00:00') : new Date(dateValue);
      if (isNaN(date.getTime())) return dateValue; // Return as-is if invalid
      
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    } catch (err) {
      return dateValue;
    }
  };

  // Convert DD/MM/YYYY to YYYY-MM-DD for internal storage
  const parseDateFromDisplay = (displayValue) => {
    if (!displayValue) return '';
    
    // Remove any non-digit characters except slashes
    const cleaned = displayValue.replace(/[^\d/]/g, '');
    
    // Try to parse DD/MM/YYYY format
    const parts = cleaned.split('/');
    if (parts.length === 3) {
      const day = parts[0].padStart(2, '0');
      const month = parts[1].padStart(2, '0');
      const year = parts[2];
      
      // Validate date
      if (day && month && year && year.length === 4) {
        const date = new Date(`${year}-${month}-${day}T00:00:00`);
        if (!isNaN(date.getTime()) && 
            date.getDate() === parseInt(day) && 
            date.getMonth() + 1 === parseInt(month)) {
          return `${year}-${month}-${day}`;
        }
      }
    }
    
    // If it's already in YYYY-MM-DD format, return as-is
    if (/^\d{4}-\d{2}-\d{2}$/.test(cleaned)) {
      return cleaned;
    }
    
    return '';
  };

  const [displayValue, setDisplayValue] = useState(formatDateForDisplay(value));

  // Update display value when value prop changes
  useEffect(() => {
    setDisplayValue(formatDateForDisplay(value));
  }, [value]);

  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [autoFocus]);

  const handleFocus = (e) => {
    setFocused(true);
    if (onFocus) {
      onFocus(e);
    }
  };

  const handleBlur = (e) => {
    setFocused(false);
    
    // Format the value on blur if it's not empty
    const parsed = parseDateFromDisplay(displayValue);
    if (parsed) {
      setDisplayValue(formatDateForDisplay(parsed));
    } else if (displayValue && displayValue.trim()) {
      // Invalid date - reset to last valid value
      setDisplayValue(formatDateForDisplay(value));
    }
    
    if (onBlur) {
      onBlur(e);
    }
  };

  const handleChange = (e) => {
    let inputValue = e.target.value;
    
    // Auto-format as user types (add slashes automatically)
    const digitsOnly = inputValue.replace(/\D/g, '');
    if (digitsOnly.length <= 2) {
      inputValue = digitsOnly;
    } else if (digitsOnly.length <= 4) {
      inputValue = `${digitsOnly.slice(0, 2)}/${digitsOnly.slice(2)}`;
    } else {
      inputValue = `${digitsOnly.slice(0, 2)}/${digitsOnly.slice(2, 4)}/${digitsOnly.slice(4, 8)}`;
    }
    
    setDisplayValue(inputValue);
    
    // Parse and convert to YYYY-MM-DD format
    const parsed = parseDateFromDisplay(inputValue);
    
    if (onChange) {
      const event = {
        ...e,
        target: {
          ...e.target,
          name,
          value: parsed || '' // Send parsed value or empty string if parsing fails
        }
      };
      onChange(event);
    }
  };

  const openDatePicker = () => {
    if (disabled || readOnly) return;
    
    // Trigger the native date picker
    if (hiddenInputRef.current) {
      // Focus first
      hiddenInputRef.current.focus();
      
      // Try showPicker() method (modern browsers)
      if (typeof hiddenInputRef.current.showPicker === 'function') {
        try {
          hiddenInputRef.current.showPicker();
          return;
        } catch (err) {
          // showPicker() failed, fall through to click
        }
      }
      
      // Fallback: trigger click event
      const clickEvent = new MouseEvent('click', {
        view: window,
        bubbles: true,
        cancelable: true
      });
      hiddenInputRef.current.dispatchEvent(clickEvent);
    }
  };

  const handleCalendarClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    openDatePicker();
  };

  const handleInputClick = (e) => {
    // Only open picker if clicking in the input field (not when typing)
    if (e.target === inputRef.current) {
      openDatePicker();
    }
  };

  const handleHiddenDateChange = (e) => {
    // When native picker is used, update both values
    const nativeValue = e.target.value;
    setDisplayValue(formatDateForDisplay(nativeValue));
    
    if (onChange) {
      const event = {
        ...e,
        target: {
          ...e.target,
          name,
          value: nativeValue
        }
      };
      onChange(event);
    }
  };

  const handleClear = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDisplayValue('');
    if (onChange) {
      const event = {
        target: {
          name,
          value: ''
        }
      };
      onChange(event);
    }
    inputRef.current?.focus();
  };

  const getInputClasses = () => {
    let classes = styles.inputField;
    if (error) classes += ` ${styles.error}`;
    // Always consider it "filled" (label floating) for date picker
    classes += ` ${styles.filled}`;
    if (label) classes += ` ${styles.hasLabel}`;
    if (allowClear && displayValue) classes += ` ${styles.hasClearButton}`;
    return classes;
  };

  const getLabelClasses = () => {
    let classes = styles.floatingLabel;
    // Always filled for date
    classes += ` ${styles.filled}`;
    if (focused) classes += ` ${styles.focused}`;
    if (error) classes += ` ${styles.error}`;
    if (disabled) classes += ` ${styles.disabled}`;
    return classes;
  };

  const getWrapperClasses = () => {
    let classes = `${styles.datePickerWrapper}`;
    if (size === 'small') classes += ` ${styles.small}`;
    if (className) classes += ` ${className}`;
    return classes;
  };

  return (
    <div className={getWrapperClasses()}>
      <div className={styles.inputContainer}>
        {/* Hidden native date input for picker functionality - positioned exactly over calendar icon */}
        <input
          ref={hiddenInputRef}
          type="date"
          name={`${name}_hidden`}
          value={value}
          onChange={handleHiddenDateChange}
          style={{
            position: 'absolute',
            right: '10px',
            top: '50%',
            transform: 'translateY(-50%)',
            width: '20px',
            height: '20px',
            opacity: 0,
            cursor: disabled || readOnly ? 'default' : 'pointer',
            zIndex: 3,
            fontSize: '16px', // Prevents zoom on iOS
            padding: 0,
            margin: 0,
            border: 'none',
            background: 'transparent',
            pointerEvents: disabled || readOnly ? 'none' : 'auto'
          }}
          tabIndex={-1}
          disabled={disabled}
          aria-hidden="true"
        />
        
        {/* Visible text input with DD/MM/YYYY format */}
        <input
          ref={inputRef}
          type="text"
          name={name}
          value={displayValue}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onClick={handleInputClick}
          placeholder={placeholder}
          disabled={disabled}
          required={required}
          readOnly={readOnly}
          className={getInputClasses()}
          maxLength={10}
          {...props}
        />
        
        {allowClear && displayValue && !disabled && !readOnly && (
          <button
            type="button"
            className={styles.clearButton}
            onClick={handleClear}
            tabIndex={-1}
          >
            <XMarkIcon />
          </button>
        )}

        <div 
          className={`${styles.calendarIcon} ${!disabled && !readOnly ? styles.clickable : ''}`}
          onClick={handleCalendarClick}
          style={{ 
            cursor: disabled || readOnly ? 'default' : 'pointer',
            pointerEvents: disabled || readOnly ? 'none' : 'auto',
            zIndex: 4
          }}
        >
          <CalendarIcon />
        </div>

        {label && (
          <label className={getLabelClasses()}>
            {label}
            {required && <span style={{ color: '#dc2626' }}> *</span>}
          </label>
        )}
      </div>
      {error && (
        <div className={styles.errorMessage}>{error}</div>
      )}
      {helperText && !error && (
        <div className={styles.helperText}>{helperText}</div>
      )}
    </div>
  );
};

export default DatePicker;